import axios, { AxiosInstance, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import {
  LoginRequest,
  LoginResponse,
  UserInfo,
  RefreshTokenRequest,
  ChangePasswordRequest,
  MfaSetupRequest,
  MfaSetupResponse,
  MfaVerifySetupRequest,
  MfaVerifyLoginRequest,
  MfaDisableRequest,
  MfaStatusResponse,
  CreateUserRequest,
  UpdateUserRequest,
  UserListResponse,
  ResetMfaRequest,
  ResetMfaResponse,
  InviteUserRequest,
  InviteUserResponse,
  AcceptInviteRequest,
} from "../types/Auth";

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5003/api";

class AuthService {
  private api: AxiosInstance;
  private tokenRefreshPromise: Promise<string> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 100000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        // Don't add Authorization header for MFA endpoints that use tempToken
        const isMfaEndpoint = config.url?.includes('/auth/mfa/');
        if (!isMfaEndpoint) {
          const token = this.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't try to refresh token for MFA endpoints - these 401s are expected for invalid codes
        const isMfaEndpoint = originalRequest?.url?.includes('/auth/mfa/');
        
        if (error.response?.status === 401 && !originalRequest._retry && !isMfaEndpoint) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.api(originalRequest);
          } catch (refreshError) {
            this.logout();
            window.location.href = "/login";
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management
  getToken(): string | null {
    return Cookies.get("authToken") || null;
  }

  private setToken(token: string): void {
    Cookies.set("authToken", token, {
      expires: 1, // 1 day
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
    });
  }

  getRefreshToken(): string | null {
    return Cookies.get("refreshToken") || null;
  }

  private setRefreshToken(refreshToken: string): void {
    Cookies.set("refreshToken", refreshToken, {
      expires: 7, // 7 days
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
    });
  }

  private clearTokens(): void {
    Cookies.remove("authToken");
    Cookies.remove("refreshToken");
  }

  // Authentication methods
  async login(email: string, password: string, trustToken?: string): Promise<LoginResponse> {
    try {
      const requestBody: LoginRequest = { email, password };
      if (trustToken) {
        requestBody.trustToken = trustToken;
      }
      const response: AxiosResponse<LoginResponse> = await this.api.post(
        "/auth/login",
        requestBody
      );

      // Log response for debugging
      console.log("Login API Response:", response.data);
      console.log("Response keys:", Object.keys(response.data || {}));

      // Handle both camelCase and PascalCase field names
      const mfaRequired =
        response.data.mfaRequired || (response.data as any).MfaRequired;
      const mfaSetupRequired =
        response.data.mfaSetupRequired ||
        (response.data as any).MfaSetupRequired;
      const tempToken =
        response.data.tempToken || (response.data as any).TempToken;

      // If MFA setup is required, return early without setting tokens
      if (mfaSetupRequired === true || mfaSetupRequired === "true") {
        console.log("MFA Setup Required");
        console.log("Response data:", response.data);
        // Return response with mfaSetupRequired flag, even if user is not present
        return {
          ...response.data,
          mfaSetupRequired: true,
          mfaRequired: false,
          user: response.data.user || {
            id: "",
            email: email,
            name: "",
            role: "user" as any,
            permissions: [],
          }, // Provide default user if not present
        };
      }

      // If MFA verification is required (MFA already set up), don't set tokens yet
      // Check explicitly for true (not just truthy) to handle false/undefined correctly
      if (mfaRequired === true || mfaRequired === "true") {
        console.log("MFA Required - tempToken:", tempToken);
        return {
          ...response.data,
          mfaRequired: true,
          mfaSetupRequired: false,
          tempToken: tempToken,
        };
      }

      const { token, refreshToken } = response.data;

      if (token && refreshToken) {
        this.setToken(token);
        this.setRefreshToken(refreshToken);
      }

      return response.data;
    } catch (error: any) {
      console.error("Login error:", error);
      throw new Error(
        error.response?.data?.message ||
          "Login failed. Please check your credentials."
      );
    }
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        await this.api.post("/auth/logout", { refreshToken });
      }
    } catch (error) {
      console.warn("Logout request failed:", error);
    } finally {
      this.clearTokens();
    }
  }

  async refreshToken(): Promise<string> {
    // Prevent multiple simultaneous refresh requests
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.tokenRefreshPromise = this.performTokenRefresh();

    try {
      const newToken = await this.tokenRefreshPromise;
      return newToken;
    } finally {
      this.tokenRefreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response: AxiosResponse<LoginResponse> = await this.api.post(
        "/auth/refresh",
        {
          refreshToken,
        } as RefreshTokenRequest
      );

      const { token, refreshToken: newRefreshToken } = response.data;

      if (!token || !newRefreshToken) {
        throw new Error("Invalid token refresh response");
      }

      this.setToken(token);
      this.setRefreshToken(newRefreshToken);

      return token;
    } catch (error: any) {
      this.clearTokens();
      throw new Error("Token refresh failed");
    }
  }

  async getCurrentUser(): Promise<UserInfo> {
    try {
      const response: AxiosResponse<UserInfo> = await this.api.get("/auth/me");
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get user information"
      );
    }
  }

  async changePassword(newPassword: string): Promise<void> {
    try {
      await this.api.post("/auth/change-password", {
        newPassword,
      } as ChangePasswordRequest);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to change password"
      );
    }
  }

  async validateToken(): Promise<boolean> {
    try {
      await this.api.get("/auth/validate");
      return true;
    } catch (error) {
      return false;
    }
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getStoredUser(): UserInfo | null {
    try {
      const userStr = Cookies.get("userInfo");
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  setStoredUser(user: UserInfo): void {
    Cookies.set("userInfo", JSON.stringify(user), {
      expires: 1,
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
    });
  }

  clearStoredUser(): void {
    Cookies.remove("userInfo");
  }

  /**
   * Accept tokens minted by the on-prem portal SSO flow. The /api/auth/sso
   * endpoint redirects the browser to /sso-callback?token=...&refresh=...
   * and the SsoCallback component calls this method to seed the cookies
   * the same way a normal login does.
   */
  acceptSsoTokens(token: string, refreshToken: string): void {
    this.setToken(token);
    this.setRefreshToken(refreshToken);
  }

  // MFA Methods
  async setupMfa(email: string): Promise<MfaSetupResponse> {
    try {
      const response: AxiosResponse<MfaSetupResponse> = await this.api.post(
        "/auth/mfa/setup",
        { email } as MfaSetupRequest
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to setup MFA");
    }
  }

  async verifyMfaSetup(email: string, code: string): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await this.api.post(
        "/auth/mfa/verify-setup",
        {
          email,
          code,
        } as MfaVerifySetupRequest
      );

      // Backend automatically logs in after MFA setup, so handle the response
      const { token, refreshToken } = response.data;

      if (token && refreshToken) {
        this.setToken(token);
        this.setRefreshToken(refreshToken);
      }

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to verify MFA setup"
      );
    }
  }

  async verifyMfaLogin(
    email: string,
    code: string,
    tempToken: string,
    trustDevice: boolean = false
  ): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await this.api.post(
        "/auth/mfa/verify-login",
        {
          email,
          code,
          tempToken,
          trustDevice,
        } as MfaVerifyLoginRequest
      );

      const { token, refreshToken, trustToken } = response.data;

      if (token && refreshToken) {
        this.setToken(token);
        this.setRefreshToken(refreshToken);
      }

      // Store trust token if returned (Remember this device)
      if (trustToken) {
        localStorage.setItem('mfaTrustToken', trustToken);
      }

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to verify MFA code"
      );
    }
  }

  async disableMfa(code: string): Promise<void> {
    try {
      await this.api.post("/auth/mfa/disable", {
        code,
      } as MfaDisableRequest);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to disable MFA");
    }
  }

  async getMfaStatus(): Promise<MfaStatusResponse> {
    try {
      const response: AxiosResponse<MfaStatusResponse> = await this.api.get(
        "/auth/mfa/status"
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get MFA status"
      );
    }
  }

  // User Management Methods (Admin only)
  async getUsers(page: number = 1, pageSize: number = 50, search?: string): Promise<UserListResponse> {
    try {
      const params: any = { page, pageSize };
      if (search) {
        params.search = search;
      }
      const response: AxiosResponse<UserListResponse> = await this.api.get(
        "/admin/users",
        { params }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }

  async getUserById(userId: string): Promise<UserInfo> {
    try {
      const response: AxiosResponse<UserInfo> = await this.api.get(
        `/admin/users/${userId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }

  async createUser(userData: CreateUserRequest): Promise<UserInfo> {
    try {
      const response: AxiosResponse<UserInfo> = await this.api.post(
        "/admin/users",
        userData
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to create user"
      );
    }
  }

  async updateUser(userData: UpdateUserRequest): Promise<UserInfo> {
    try {
      const { id, ...updateData } = userData;
      const response: AxiosResponse<UserInfo> = await this.api.put(
        `/admin/users/${id}`,
        updateData
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      await this.api.delete(`/admin/users/${userId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }

  // Passkey/WebAuthn Methods
  async passkeyRegisterStart(email: string): Promise<any> {
    try {
      const response = await this.api.post("/auth/passkey/register/start", { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to start passkey registration");
    }
  }

  async passkeyRegisterComplete(email: string, attestation: string): Promise<any> {
    try {
      const response = await this.api.post("/auth/passkey/register/complete", {
        email,
        attestationResponse: attestation,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to complete passkey registration");
    }
  }

  async passkeyLoginStart(email?: string): Promise<any> {
    try {
      const response = await this.api.post("/auth/passkey/login/start", { email: email || null });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to start passkey login");
    }
  }

  async passkeyLoginComplete(assertion: string): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await this.api.post(
        "/auth/passkey/login/complete",
        { assertionResponse: assertion }
      );

      const { token, refreshToken } = response.data;

      if (token && refreshToken) {
        this.setToken(token);
        this.setRefreshToken(refreshToken);
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to complete passkey login");
    }
  }

  // Invite Methods
  async inviteUser(data: InviteUserRequest): Promise<InviteUserResponse> {
    try {
      const response: AxiosResponse<InviteUserResponse> = await this.api.post(
        "/admin/users/invite",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to invite user");
    }
  }

  async validateInvite(token: string): Promise<{ email: string }> {
    try {
      const response = await this.api.get(`/auth/invite/validate/${token}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Invalid or expired invite");
    }
  }

  async acceptInvite(token: string, password: string, confirmPassword: string): Promise<any> {
    try {
      const response = await this.api.post("/auth/invite/accept", {
        token,
        password,
        confirmPassword,
      } as AcceptInviteRequest);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to accept invite");
    }
  }

  async resetUserPasskeys(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.api.post(
        `/admin/users/${userId}/reset-passkeys`,
        {}
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to reset user passkeys"
      );
    }
  }

  async resetUserMfa(userId: string): Promise<ResetMfaResponse> {
    try {
      const response: AxiosResponse<ResetMfaResponse> = await this.api.post(
        `/admin/users/${userId}/reset-mfa`,
        {} as ResetMfaRequest
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to reset user MFA"
      );
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
